import { useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useSelectedEnvironmentKey } from '@gonfalon/app-context/useSelectedEnvironmentKey';
import { ContextKindSelector } from '@gonfalon/contexts/components/ContextKindSelector';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { isLDHosted } from '@gonfalon/metrics/isLDHosted';
import { toContextKinds, toDataSourcesMetricDetails, toHref } from '@gonfalon/navigator/navigators';
import type { RandomizationUnitRep } from '@gonfalon/openapi/types';
import { getMetricDataSourceByKeyQuery } from '@gonfalon/rest-api/queries/metricDataSource';
import { useProjectKey } from '@gonfalon/router/useProjectKey';
import { NewTabLink } from '@gonfalon/ui/NewTabLink';
import { Alert } from '@launchpad-ui/components';
import { useQuery } from '@tanstack/react-query';

import type { AnalysisUnitOption, MetricForm } from '../../../CreateMetricModal/formUtils';
import { useProjectExperimentationSettings } from '../useProjectExperimentationSettings';

import styles from './MetricAnalysisUnits.module.css';

type RandomizationUnit = RandomizationUnitRep;

interface MetricAnalysisUnitsProps {
  onSelect?: (selection: string[], options: AnalysisUnitOption[], meta?: { isAutoSelected?: boolean }) => void;
  initialValues?: string[];
  dataSourceKey?: string;
  /** Stretches the selector to the row's width, as the sectioned definition rows do. */
  shouldFillWidth?: boolean;
  /** Set when a row's visible label names this selector, replacing its own `aria-label`. */
  ariaLabelledBy?: string;
}

/**
 * The validation message shown when a metric's saved analysis unit is not available for the
 * selected data source. The advice depends on how many units the source offers.
 */
export function getAnalysisUnitErrorMessage({
  unitName,
  isExternalDataSource,
  alternativeCount,
  onlyAlternative,
}: {
  unitName: string;
  isExternalDataSource: boolean;
  alternativeCount: number;
  onlyAlternative?: string;
}): string {
  if (isExternalDataSource) {
    if (alternativeCount === 0) {
      return msg('metrics.metricDefinition.noContextKindsEnabled');
    }
    if (onlyAlternative) {
      return msg('metrics.metricDefinition.unitNotEnabledSelectOnly', { unitName, onlyAlternative });
    }
    return msg('metrics.metricDefinition.unitNotEnabled', { unitName });
  }

  if (alternativeCount === 0) {
    return msg('metrics.metricDefinition.unitMissingNoAlternatives', { unitName });
  }
  if (onlyAlternative) {
    return msg('metrics.metricDefinition.unitMissingSelectOnly', { unitName, onlyAlternative });
  }
  return msg('metrics.metricDefinition.unitMissing', { unitName });
}

const useMetricAnalysisUnits = ({ dataSourceKey }: { dataSourceKey?: string }) => {
  const projectKey = useProjectKey();
  const isExternalDataSource = typeof dataSourceKey === 'string' && !isLDHosted(dataSourceKey);

  const {
    data: settings,
    isPending,
    isError,
  } = useProjectExperimentationSettings({
    projectKey,
  });
  const {
    data: dataSource,
    isPending: isPendingDataSource,
    isError: isErrorDataSource,
  } = useQuery({
    ...getMetricDataSourceByKeyQuery({ projKey: projectKey, dataSourceKey: dataSourceKey ?? '' }),
    enabled: isExternalDataSource,
  });

  const dataSourceContextKeys = useMemo(() => Object.keys(dataSource?.columnMappings?.contexts ?? {}), [dataSource]);

  return {
    data: isExternalDataSource
      ? settings?.randomizationUnits?.filter(
          (ru) => ru.randomizationUnit && dataSourceContextKeys.includes(ru.randomizationUnit),
        )
      : settings?.randomizationUnits,
    isPending: isExternalDataSource ? isPendingDataSource || isPending : isPending,
    isError: isExternalDataSource ? isErrorDataSource || isError : isError,
  };
};

export function MetricAnalysisUnits({
  onSelect,
  initialValues,
  dataSourceKey,
  shouldFillWidth = false,
  ariaLabelledBy,
}: MetricAnalysisUnitsProps) {
  const widthClassName = shouldFillWidth ? styles.fullWidthAnalysisUnits : undefined;
  const { data: analysisUnits, isPending, isError } = useMetricAnalysisUnits({ dataSourceKey });
  // Snapshotted, not read live: RHF mutates formState.defaultValues on setValue, which would
  // let this flip from edit to create after the auto-default effect fires.
  const { formState, setError, clearErrors } = useFormContext<MetricForm>();
  const [initialAnalysisUnits] = useState(() => formState.defaultValues?.analysisUnits);
  const isEditingExistingMetric = Boolean(initialAnalysisUnits?.length);
  const lastProcessedDataSourceKeyRef = useRef<string | undefined>(dataSourceKey);
  const [committedDataSourceKey, setCommittedDataSourceKey] = useState(dataSourceKey);

  const options: AnalysisUnitOption[] = useMemo(() => analysisUnits?.map(toOption) ?? [], [analysisUnits]);
  const defaultValues = useMemo(
    () =>
      initialValues && initialValues.length > 0
        ? initialValues
        : [options?.find((o) => o.default)?.value ?? options[0]?.value],
    [initialValues, options],
  );

  // A warehouse source only supports its mapped context kinds, so moving to one resets the unit
  // (in the effect below) even when a metric already had one set.
  const switchedFromLdToExternalDataSource =
    committedDataSourceKey !== dataSourceKey &&
    isLDHosted(committedDataSourceKey) &&
    typeof dataSourceKey === 'string' &&
    !isLDHosted(dataSourceKey);

  const selectionsCompatibleWithOptions =
    !initialValues?.length || initialValues.every((val) => options.some((option) => option.value === val));

  if (options.length > 0 && committedDataSourceKey !== dataSourceKey && selectionsCompatibleWithOptions) {
    setCommittedDataSourceKey(dataSourceKey);
  }

  // A saved unit missing from a settled option list was deleted from the project. Deleting every
  // unit empties the list, so the fetch state — not `options.length` — is what says it settled.
  const willAutoSwitchToExternalDefault = switchedFromLdToExternalDataSource && options.length > 0;
  const orphanedValue =
    isEditingExistingMetric && !isPending && !isError && !willAutoSwitchToExternalDefault
      ? initialValues?.find((value) => !options.some((option) => option.value === value))
      : undefined;
  const orphanedUnitName = orphanedValue
    ? (initialAnalysisUnits?.find((unit) => unit?.value === orphanedValue)?.label ?? orphanedValue)
    : undefined;
  const isOrphaned = Boolean(orphanedUnitName);

  // On an external source the unit isn't gone, just unmapped, so the copy and link differ.
  const isExternalDataSource = typeof dataSourceKey === 'string' && !isLDHosted(dataSourceKey);

  const alternativeCount = options.length;
  const onlyAlternative = alternativeCount === 1 ? options[0]?.label : undefined;

  useEffect(() => {
    if (!isOrphaned) {
      clearErrors('analysisUnits');
      return;
    }

    setError('analysisUnits', {
      type: 'manual',
      message: getAnalysisUnitErrorMessage({
        unitName: orphanedUnitName ?? '',
        isExternalDataSource,
        alternativeCount,
        onlyAlternative,
      }),
    });
  }, [isOrphaned, orphanedUnitName, isExternalDataSource, alternativeCount, onlyAlternative, setError, clearErrors]);

  useEffect(() => {
    // Leave a persisted unit alone (edit) or a seeded unit in place to be validated
    // (clone); only the create flow — or a switch to an external data source —
    // auto-defaults a selection.
    const dataSourceKeyChanged = lastProcessedDataSourceKeyRef.current !== dataSourceKey;
    const switchedFromLdToExternal =
      dataSourceKeyChanged &&
      isLDHosted(lastProcessedDataSourceKeyRef.current) &&
      typeof dataSourceKey === 'string' &&
      !isLDHosted(dataSourceKey);

    if (isEditingExistingMetric && !switchedFromLdToExternal) {
      return;
    }

    // There are current selections that are not compatible with the newly selected data source
    const incompatibleSelection =
      Array.isArray(initialValues) && initialValues.some((val) => !options.some((option) => option?.value === val));
    // There are no selections at all (eg. no defaultValue passed to the form)
    const noSelectedValues = !initialValues?.length && options.length;
    // If any of the previous conditions are true, we should update the form value to the correct default for the
    // selected data source
    const shouldUpdateFormValue = dataSourceKeyChanged || incompatibleSelection || noSelectedValues;

    if (options.length && shouldUpdateFormValue) {
      const firstOption = options[0]?.value;
      const defaultOption = options.find((o) => o.default)?.value;

      onSelect?.([defaultOption || firstOption], options, { isAutoSelected: true });
    }

    if (options.length) {
      lastProcessedDataSourceKeyRef.current = dataSourceKey;
    }
  }, [dataSourceKey, options, initialValues, onSelect, isEditingExistingMetric]);

  if (isError) {
    return (
      <Alert status="error">
        <Message id="metrics.metricDefinition.analysisUnitsLoadError" />
      </Alert>
    );
  }

  return (
    <div data-test-id="metric-analysis-units" className={widthClassName}>
      <ContextKindSelector
        className={styles.selector}
        key={dataSourceKey}
        contextKinds={[
          ...options.map((option) => ({ key: option.value, name: option.label })),
          ...(orphanedValue ? [{ key: orphanedValue, name: orphanedUnitName ?? orphanedValue }] : []),
        ]}
        selectionMode="multiple"
        isSearchable
        shortenLabels
        disallowEmptySelection
        isDisabled={isPending}
        isInvalid={isOrphaned}
        selectedKeys={isPending ? [] : defaultValues.filter((value): value is string => typeof value === 'string')}
        onSelectionChange={(keys) => {
          if (keys.length > 0) {
            onSelect?.(keys, options);
          }
        }}
        placeholder={msg('metrics.metricDefinition.unitsSearchPlaceholder')}
        aria-label={ariaLabelledBy ? undefined : msg('metrics.metricDefinition.analysisUnitsLabel')}
        aria-labelledby={ariaLabelledBy}
      />
    </div>
  );
}

function toOption(r: RandomizationUnit): AnalysisUnitOption {
  const value = r.randomizationUnit ?? '';
  return {
    label: r._displayName ?? value,
    value,
    default: r.default,
  };
}

interface AnalysisUnitsErrorProps {
  /** The analysis unit form error message, e.g. errors.analysisUnits?.message. */
  message?: string;
  /** The currently selected data source key, used to pick the relevant recovery link. */
  dataSourceKey?: string;
  /** Set by the sectioned Simple layout, which drops the data source link (see below). */
  useSectionedLayout?: boolean;
}

/**
 * The analysis unit form error plus the recovery link its message points at: the data source
 * configuration for an unmapped unit, the project's context kinds for a deleted one.
 *
 * Not FieldGroup's `errorMessage`, which would push its invalid state into every descendant
 * Select and redden the whole definition sentence.
 */
export function AnalysisUnitsError({ message, dataSourceKey, useSectionedLayout = false }: AnalysisUnitsErrorProps) {
  const projectKey = useProjectKey();
  const environmentKey = useSelectedEnvironmentKey();

  if (!message) {
    return null;
  }

  const isExternalDataSource = Boolean(dataSourceKey) && !isLDHosted(dataSourceKey);

  let link;
  if (!isExternalDataSource) {
    link = (
      <NewTabLink
        href={toHref(toContextKinds({ projectKey, environmentKey }))}
        text={msg('metrics.metricDefinition.contextKindSettingsLink')}
      />
    );
  } else if (!useSectionedLayout && dataSourceKey !== undefined) {
    link = (
      <NewTabLink
        href={toHref(toDataSourcesMetricDetails({ projectKey, dataSourceKey }))}
        text={msg('metrics.metricDefinition.dataSourceConfigurationLink')}
      />
    );
  }

  return (
    <span className={styles.analysisUnitsError} role="alert">
      {message}
      {link ? <> {link}</> : null}
    </span>
  );
}
